import cx from 'classnames';
import type { FunctionComponent } from 'react';
import { useRef } from 'react';

import type { BoardSwitcherTestIds } from '@trello/test-ids';
import { getTestId } from '@trello/test-ids';

import { BoardSwitcherTile } from './BoardSwitcherTile';
import { useStarredBoardDragAndDrop } from './useStarredBoardDragAndDrop';

import * as styles from './DraggableStarredBoard.module.less';

interface DraggableStarredBoardProps {
  boardId: string;
  layout?: 'grid' | 'list';
}

export const DropIndicator: FunctionComponent<{
  role: 'gridcell' | 'listitem';
}> = ({ role }) => {
  return <div className={styles.dropIndicator} role={role}></div>;
};

export const DraggableStarredBoard: FunctionComponent<
  DraggableStarredBoardProps
> = ({ boardId, layout = 'grid' }) => {
  const ref = useRef<HTMLDivElement>(null);

  const ariaRole = layout === 'list' ? 'listitem' : 'gridcell';
  const { dragState, dropPreviewClosestEdge } = useStarredBoardDragAndDrop({
    ref,
    selectedBoardId: boardId,
    layout,
  });

  const dropIndicatorBefore =
    (layout === 'list' && dropPreviewClosestEdge === 'top') ||
    (layout === 'grid' && dropPreviewClosestEdge === 'left');

  const dropIndicatorAfter =
    (layout === 'list' && dropPreviewClosestEdge === 'bottom') ||
    (layout === 'grid' && dropPreviewClosestEdge === 'right');

  return (
    <>
      {dropIndicatorBefore && <DropIndicator role={ariaRole} />}
      <div
        ref={ref}
        id={boardId}
        className={cx(styles.listItem, {
          [styles.listItemGrid]: layout === 'grid',
          [styles.isDragging]: dragState === 'dragging',
          [styles.isMoving]: dragState === 'moving',
        })}
        data-testid={getTestId<BoardSwitcherTestIds>(
          'starred-boards-board-title',
        )}
        role={ariaRole}
      >
        <BoardSwitcherTile
          boardId={boardId}
          section="starred"
          className={styles.switcherTileStyleOverwrite}
        />
      </div>
      {dropIndicatorAfter && <DropIndicator role={ariaRole} />}
    </>
  );
};
